/**
 * 🧮 BALANCE UTILITY FUNCTIONS
 *
 * Helper functions for calculating game balance values.
 * All functions use the BALANCE config from config.js.
 */

import { BALANCE_CONFIG } from './config';

// ===========================================
// 🏗️ BUILDING HELPERS
// ===========================================

// Re-export from buildings module for backwards compatibility
export { calculateBuildingCost } from '../../modules/buildings';

// ===========================================
// 🔬 RESEARCH HELPERS
// ===========================================

/**
 * Get base research time for a tech tier (in seconds)
 */
export const getBaseResearchTime = (tier) => {
  return BALANCE_CONFIG.tech.baseTimes[tier] || BALANCE_CONFIG.tech.baseTimes[11];
};

/**
 * Calculate actual research time with speed bonuses
 * @param {number} tier - Tech tier
 * @param {number} speedMultiplier - Total speed multiplier (1.0 = normal, 2.0 = 2x speed)
 * @returns {number} Time in seconds
 */
export const calculateResearchTime = (tier, speedMultiplier = 1.0) => {
  const baseTime = getBaseResearchTime(tier);
  const actualTime = baseTime / speedMultiplier;
  return Math.max(actualTime, BALANCE_CONFIG.tech.minResearchTime);
};

/**
 * Get current research speed multiplier based on purchased upgrades
 */
export const getResearchSpeedMultiplier = (purchasedResearchUpgrades = []) => {
  let multiplier = BALANCE_CONFIG.tech.baseSpeedMultiplier;

  for (const upgrade of BALANCE_CONFIG.researchUpgrades) {
    if (purchasedResearchUpgrades.includes(upgrade.id)) {
      multiplier += upgrade.speedBonus;
    }
  }

  return multiplier;
};

// ===========================================
// 🔬 TECHNOLOGY COST HELPERS
// ===========================================

/**
 * Get the cost multiplier for a tech based on its tier
 * @param {number} tier - Tech tier (1-11)
 * @returns {number} Combined multiplier (global * tier)
 */
export const getTechCostMultiplier = (tier) => {
  const global = BALANCE_CONFIG.tech.globalCostMultiplier || 1.0;
  const tierMult = BALANCE_CONFIG.tech.tierCostMultipliers?.[tier] || 1.0;
  return global * tierMult;
};

/**
 * Apply balance multipliers to technology costs
 * @param {object} cost - Original cost object { knowledge: 100, iron: 50 }
 * @param {number} tier - Tech tier (1-11)
 * @returns {object} Scaled costs
 */
export const getScaledTechCost = (cost, tier) => {
  if (!cost) return {};
  const multiplier = getTechCostMultiplier(tier);
  const scaled = {};
  for (const [resource, amount] of Object.entries(cost)) {
    scaled[resource] = Math.ceil(amount * multiplier);
  }
  return scaled;
};

// ===========================================
// ⏱️ TIME FORMATTING
// ===========================================

/**
 * Format seconds into human readable time
 */
export const formatTime = (seconds) => {
  if (seconds < 60) {
    return `${Math.ceil(seconds)}s`;
  } else if (seconds < 3600) {
    const mins = Math.floor(seconds / 60);
    const secs = Math.ceil(seconds % 60);
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  } else {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
};

// ===========================================
// 📈 PRODUCTION HELPERS
// ===========================================

/**
 * Calculate production with all multipliers
 */
export const calculateProduction = (baseRate, population = 0) => {
  let multiplier = BALANCE_CONFIG.multipliers.global;

  // Population bonus
  const popBonus = Math.min(
    population * BALANCE_CONFIG.multipliers.perPopulation,
    BALANCE_CONFIG.multipliers.maxPopulationBonus
  );
  multiplier += popBonus;

  return baseRate * multiplier * BALANCE_CONFIG.ticks.productionMultiplier;
};

// ===========================================
// 🏰 KINGDOM TIER HELPERS
// ===========================================

/**
 * Get kingdom tier based on building count
 */
export const getKingdomTier = (totalBuildings) => {
  const k = BALANCE_CONFIG.kingdom;

  if (totalBuildings >= k.tier10) return 10;
  if (totalBuildings >= k.tier9) return 9;
  if (totalBuildings >= k.tier8) return 8;
  if (totalBuildings >= k.tier7) return 7;
  if (totalBuildings >= k.tier6) return 6;
  if (totalBuildings >= k.tier5) return 5;
  if (totalBuildings >= k.tier4) return 4;
  if (totalBuildings >= k.tier3) return 3;
  if (totalBuildings >= k.tier2) return 2;
  if (totalBuildings >= k.tier1) return 1;
  return 0;
};

/**
 * Get player's progression era based on RESEARCH (not buildings)
 * This is the "true" era used for gameplay scaling.
 *
 * Used for:
 *   - Biome spawn weights
 *   - Gather yield scaling
 *   - Exploration event rewards/penalties
 *
 * @param {array} researchedTechs - Array of completed tech IDs
 * @param {object} technologies - Technologies data object
 * @returns {number} Era 1-11
 */
export const getPlayerEra = (researchedTechs, technologies) => {
  if (!researchedTechs || researchedTechs.length === 0) return 1;
  return Math.max(...researchedTechs.map(id => technologies[id]?.tier || 1));
};

// ===========================================
// 👆 CLICK POWER HELPERS
// ===========================================

/**
 * Get current click power based on purchased upgrades
 */
export const getClickPower = (purchasedClickUpgrades = []) => {
  if (purchasedClickUpgrades.length === 0) {
    return BALANCE_CONFIG.clicking.baseValue;
  }
  // Find highest purchased upgrade
  const upgrades = BALANCE_CONFIG.clickUpgrades;
  let highestPower = BALANCE_CONFIG.clicking.baseValue;

  for (const upgrade of upgrades) {
    if (purchasedClickUpgrades.includes(upgrade.id)) {
      highestPower = upgrade.clickPower;
    }
  }

  return highestPower;
};

// ===========================================
// 📜 QUEST BALANCE HELPERS
// ===========================================

/**
 * Get the reward multiplier for a quest based on its chapter
 * @param {number} chapter - Quest chapter (1-10)
 * @returns {number} Combined multiplier (global * chapter)
 */
export const getQuestRewardMultiplier = (chapter) => {
  const global = BALANCE_CONFIG.quests.globalRewardMultiplier || 1.0;
  const chapterMult = BALANCE_CONFIG.quests.chapterRewardMultipliers?.[chapter] || 1.0;
  return global * chapterMult;
};

/**
 * Get the objective multiplier for a quest based on its chapter
 * @param {number} chapter - Quest chapter (1-10)
 * @returns {number} Combined multiplier (global * chapter)
 */
export const getQuestObjectiveMultiplier = (chapter) => {
  const global = BALANCE_CONFIG.quests.globalObjectiveMultiplier || 1.0;
  const chapterMult = BALANCE_CONFIG.quests.chapterObjectiveMultipliers?.[chapter] || 1.0;
  return global * chapterMult;
};

/**
 * Apply balance multipliers to quest rewards
 * @param {object} rewards - Original reward object { wood: 50, gold: 25 }
 * @param {number} chapter - Quest chapter (1-10)
 * @returns {object} Scaled rewards
 */
export const getScaledQuestRewards = (rewards, chapter) => {
  if (!rewards) return {};
  const multiplier = getQuestRewardMultiplier(chapter);
  const scaled = {};
  for (const [resource, amount] of Object.entries(rewards)) {
    scaled[resource] = Math.floor(amount * multiplier);
  }
  return scaled;
};

/**
 * Apply balance multipliers to quest objectives
 * Only scales count-based objectives (not tech/building targets)
 * @param {array} objectives - Original objectives array
 * @param {number} chapter - Quest chapter (1-10)
 * @returns {array} Scaled objectives
 */
export const getScaledQuestObjectives = (objectives, chapter) => {
  if (!objectives) return [];
  const multiplier = getQuestObjectiveMultiplier(chapter);

  return objectives.map(obj => {
    // Only scale objectives with a 'count' property
    // Skip tech/building targets (type: 'tech', 'building' with count=1)
    if (obj.count && obj.count > 1) {
      return { ...obj, count: Math.ceil(obj.count * multiplier) };
    }
    return obj;
  });
};

// ===========================================
// 🏗️ BUILDING CAPACITY HELPERS
// ===========================================

/**
 * Count buildings of a type placed in outpost slots
 * @param {string} buildingId - The building type
 * @param {object} outposts - Outpost data { nodeId: { buildingSlots: [...] } }
 * @returns {number} Number of this building in outpost slots
 */
export const getOutpostBuildingCount = (buildingId, outposts = {}) => {
  let count = 0;
  for (const outpost of Object.values(outposts)) {
    if (outpost?.buildingSlots && !outpost.isConstructing) {
      count += outpost.buildingSlots.filter(slot => slot === buildingId).length;
    }
  }
  return count;
};

/**
 * Get the capacity for panel buildings based on tile buildings AND outpost buildings
 * Each tile/outpost building grants capacityPerTile (default 5) panel buildings
 * @param {string} buildingId - The building type
 * @param {object} worldBuildingCounts - Count of buildings placed on world tiles
 * @param {object} outposts - Outpost data { nodeId: { buildingSlots: [...] } }
 * @returns {number} Maximum number of panel buildings allowed
 */
export const getBuildingCapacity = (buildingId, worldBuildingCounts = {}, outposts = {}) => {
  const tilesBuilt = worldBuildingCounts[buildingId] || 0;
  const outpostBuilt = getOutpostBuildingCount(buildingId, outposts);
  const capacityPerTile = BALANCE_CONFIG.buildings.capacityPerTile || 5;
  return (tilesBuilt + outpostBuilt) * capacityPerTile;
};

/**
 * Get the current panel building count (excludes tile AND outpost buildings)
 * @param {string} buildingId - The building type
 * @param {object} buildingCounts - Total building counts
 * @param {object} worldBuildingCounts - Count of buildings on world tiles
 * @param {object} outposts - Outpost data { nodeId: { buildingSlots: [...] } }
 * @returns {number} Number of panel-built buildings
 */
export const getPanelBuildingCount = (buildingId, buildingCounts = {}, worldBuildingCounts = {}, outposts = {}) => {
  const total = buildingCounts[buildingId] || 0;
  const tileBuilt = worldBuildingCounts[buildingId] || 0;
  const outpostBuilt = getOutpostBuildingCount(buildingId, outposts);
  return Math.max(0, total - tileBuilt - outpostBuilt);
};

/**
 * Check if more panel buildings can be purchased (capacity check)
 * Returns info for both internal logic and display.
 *
 * Display: shows total buildings vs max (tile + outpost + panel capacity)
 * Example: 1 tile + 1 outpost + 2 panel = owned:4, maxOwned:12 (2 source buildings × 5 capacity + 2 source)
 *
 * @param {string} buildingId - The building type
 * @param {object} buildingCounts - Total building counts
 * @param {object} worldBuildingCounts - Count of buildings on world tiles
 * @param {object} outposts - Outpost data { nodeId: { buildingSlots: [...] } }
 * @returns {{ canBuild, current, capacity, tilesBuilt, outpostBuilt, remaining, owned, maxOwned }}
 */
export const getBuildingCapacityInfo = (buildingId, buildingCounts = {}, worldBuildingCounts = {}, outposts = {}) => {
  const tilesBuilt = worldBuildingCounts[buildingId] || 0;
  const outpostBuilt = getOutpostBuildingCount(buildingId, outposts);
  const panelCapacity = getBuildingCapacity(buildingId, worldBuildingCounts, outposts);
  const panelCurrent = getPanelBuildingCount(buildingId, buildingCounts, worldBuildingCounts, outposts);
  const canBuild = panelCurrent < panelCapacity;

  // For display: show total owned vs total max (including tile + outpost buildings)
  const owned = buildingCounts[buildingId] || 0;
  const sourceBuildings = tilesBuilt + outpostBuilt; // Buildings that grant capacity
  const maxOwned = sourceBuildings + panelCapacity; // source buildings + panel capacity

  return {
    canBuild,
    current: panelCurrent,      // Panel buildings only (for internal logic)
    capacity: panelCapacity,    // Panel capacity only (for internal logic)
    tilesBuilt,
    outpostBuilt,               // NEW: Buildings in outpost slots
    remaining: Math.max(0, panelCapacity - panelCurrent),
    // For display purposes:
    owned,                      // Total buildings (tile + outpost + panel)
    maxOwned,                   // Total max (source + panel capacity)
  };
};
